import { useMemo, useState, useEffect } from 'react';
import { Outlet, useLocation, useNavigate, Link } from 'react-router-dom';
import { Layout, Menu, Avatar, Dropdown, Badge, Input, Button, Drawer } from 'antd';
import type { MenuProps } from 'antd';
import {
  LayoutDashboard,
  ListTodo,
  GitBranchPlus,
  FileSignature,
  Bell,
  BarChart3,
  Users,
  ScrollText,
  Search,
  Plus,
  LogOut,
  User as UserIcon,
  Settings,
  Menu as MenuIcon,
  X,
  FolderTree,
  Building2,
  Tags,
  Flag,
  FileText,
  Shield,
  MessageCircle,
  FolderArchive,
  FolderKanban,
  Cog,
  Inbox,
  Heart,
  ChevronLeft,
  ChevronRight,
  Mic,
  Cpu,
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { ChatNotifier, NotificationPermissionButton } from '@/components/common/ChatNotifier';
import { useSystemSettings } from '@/contexts/SystemSettingsContext';
import { useAuthStore } from '@/store/authStore';
import { useUIStore } from '@/store/uiStore';
import { notificationService } from '@/services/notificationService';
import { chatService } from '@/services/chatService';
import { useNotificationsHub } from '@/hooks/useNotificationsHub';
import { useBreakpoint } from '@/hooks/useBreakpoint';

const { Header, Sider, Content } = Layout;

export function MainLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, hasAnyRole, hasRole, hasPermission, hasAnyPermission } = useAuthStore();
  const { settings, hasLogo, logoVersion } = useSystemSettings();
  const { sidebarCollapsed, toggleSidebar } = useUIStore();
  const { isMobile, isTablet } = useBreakpoint();

  useNotificationsHub();

  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  // V1.0.35: Global search trên header — Enter → navigate /tasks với query
  const [globalSearch, setGlobalSearch] = useState('');
  const handleGlobalSearch = (value: string) => {
    const q = value.trim();
    if (!q) return;
    navigate(`/tasks?search=${encodeURIComponent(q)}`);
    setGlobalSearch('');
    setMobileSearchOpen(false);
  };

  const { data: unreadCount = 0 } = useQuery({
    queryKey: ['notifications', 'unread-count'],
    queryFn: () => notificationService.getUnreadCount(),
    refetchInterval: 30_000,
  });

  const { data: chatRooms = [] } = useQuery({
    queryKey: ['chat-rooms'],
    queryFn: chatService.listRooms,
    refetchInterval: 30_000,
  });
  const chatUnread = chatRooms.reduce((sum, r) => sum + r.unreadCount, 0);

  const menuItems: MenuProps['items'] = useMemo(() => {
    const items: MenuProps['items'] = [
      { key: '/dashboard', icon: <LayoutDashboard size={16} />, label: <Link to="/dashboard">Trang chủ</Link> },
    ];

    // Task
    if (hasPermission('task:view')) {
      items.push({ key: '/tasks', icon: <ListTodo size={16} />, label: <Link to="/tasks">Task của tôi</Link> });
    }

    // Dự án (project management) - mọi user đăng nhập đều thấy được
    items.push({
      key: '/projects',
      icon: <FolderKanban size={16} />,
      label: <Link to="/projects">Dự án</Link>,
    });

    // Chat (ai cũng có)
    items.push({
      key: '/chat',
      icon: <Badge count={chatUnread} size="small" offset={[6, -2]}><MessageCircle size={16} /></Badge>,
      label: <Link to="/chat">Tin nhắn</Link>,
    });

    // Signing
    if (hasPermission('signing:view')) {
      const sigChildren: any[] = [];
      if (hasPermission('signing:create')) {
        sigChildren.push({ key: '/signing/new', label: <Link to="/signing/new">+ Trình ký mới</Link> });
      }
      sigChildren.push({ key: '/signing', label: <Link to="/signing">Danh sách ký</Link> });
      items.push({
        key: 'signing',
        icon: <FileSignature size={16} />,
        label: 'Ký điện tử',
        children: sigChildren,
      });
    }

    // Documents
    if (hasPermission('document:view')) {
      items.push({
        key: '/documents',
        icon: <FolderArchive size={16} />,
        label: <Link to="/documents">Quản lý tài liệu</Link>,
      });
    }

    // Mẫu văn bản — user dùng mẫu để tạo file PDF nhanh
    if (hasPermission('template:use')) {
      items.push({
        key: '/templates',
        icon: <FileText size={16} />,
        label: <Link to="/templates">Tạo từ mẫu</Link>,
      });
    }

    // Thông báo giờ nằm trong submenu Quản trị (xem bên dưới). Header có icon chuông cho truy cập nhanh.

    // Reports
    if (hasPermission('report:view')) {
      items.push({
        key: 'reports-group',
        icon: <BarChart3 size={16} />,
        label: 'Báo cáo',
        children: [
          { key: '/reports', label: <Link to="/reports">Tổng quan</Link> },
          { key: '/reports/kpi', label: <Link to="/reports/kpi">💰 KPI Doanh thu</Link> },
        ],
      });
    }

    // Dashboard nâng cao (Dept head + BOD)
    if (hasAnyPermission(['report:view_executive']) || hasRole('DEPT_HEAD' as any)) {
      const dashChildren: any[] = [];
      if (hasRole('DEPT_HEAD' as any) || hasPermission('report:view_executive')) {
        dashChildren.push({ key: '/dashboard/department', label: <Link to="/dashboard/department">Trưởng khoa</Link> });
      }
      if (hasPermission('report:view_executive')) {
        dashChildren.push({ key: '/dashboard/executive', label: <Link to="/dashboard/executive">Lãnh đạo</Link> });
      }
      if (dashChildren.length > 0) {
        items.push({
          key: 'dashboards',
          icon: <GitBranchPlus size={16} />,
          label: 'Dashboard nâng cao',
          children: dashChildren,
        });
      }
    }

    // Danh mục (dept + category) — yêu cầu dept:manage HOẶC document:manage_categories
    if (hasAnyPermission(['dept:manage', 'document:manage_categories', 'role:manage'])) {
      const catChildren: any[] = [];
      if (hasPermission('dept:manage')) {
        catChildren.push({ key: '/categories/departments', icon: <Building2 size={13} />, label: <Link to="/categories/departments">Khoa / Phòng</Link> });
        // V1.0.32: Nhóm khoa phòng
        catChildren.push({ key: '/categories/dept-groups', icon: <FolderArchive size={13} />, label: <Link to="/categories/dept-groups">Nhóm khoa phòng</Link> });
      }
      // Các danh mục khác (task-cat, priority, label, template) hầu như ai quản trị cũng vào được
      if (hasAnyPermission(['dept:manage', 'document:manage_categories'])) {
        catChildren.push(
          { key: '/categories/task-categories', icon: <ListTodo size={13} />, label: <Link to="/categories/task-categories">Loại công việc</Link> },
          { key: '/categories/priorities', icon: <Flag size={13} />, label: <Link to="/categories/priorities">Độ ưu tiên</Link> },
          { key: '/categories/labels', icon: <Tags size={13} />, label: <Link to="/categories/labels">Nhãn / Tag</Link> },
          { key: '/categories/print-templates', icon: <FileText size={13} />, label: <Link to="/categories/print-templates">Mẫu biểu in</Link> },
        );
      }
      if (hasPermission('role:manage')) {
        catChildren.push({ key: '/categories/roles', icon: <Shield size={13} />, label: <Link to="/categories/roles">Vai trò & Quyền</Link> });
      }
      if (catChildren.length > 0) {
        items.push({
          key: 'categories',
          icon: <FolderTree size={16} />,
          label: 'Danh mục',
          children: catChildren,
        });
      }
    }

    // Quản trị (admin)
    const adminChildren: any[] = [];

    // Thông báo — ai cũng thấy được, đặt đầu tiên trong submenu
    adminChildren.push({ key: '/notifications', icon: <Bell size={14} />, label: <Link to="/notifications">Thông báo</Link> });

    if (hasPermission('user:view')) {
      adminChildren.push({ key: '/admin/users', icon: <Users size={14} />, label: <Link to="/admin/users">Người dùng</Link> });
    }
    if (hasPermission('document:manage_categories')) {
      adminChildren.push({ key: '/admin/categories', icon: <FolderArchive size={14} />, label: <Link to="/admin/categories">Danh mục tài liệu</Link> });
    }
    if (hasPermission('template:manage')) {
      adminChildren.push({ key: '/admin/templates', icon: <FileText size={14} />, label: <Link to="/admin/templates">Quản lý mẫu</Link> });
    }
    if (hasPermission('audit:view')) {
      adminChildren.push({ key: '/admin/audit-logs', icon: <ScrollText size={14} />, label: <Link to="/admin/audit-logs">Nhật ký hệ thống</Link> });
    }
    // V1.0.37: Hộp thư lắng nghe — admin/BOD/feedback:view_inbox
    if (hasAnyRole(['SYS_ADMIN', 'BOD']) || hasPermission('feedback:view_inbox')) {
      adminChildren.push({
        key: 'feedback-menu',
        icon: <Heart size={14} />,
        label: 'Hộp thư lắng nghe',
        children: [
          { key: '/admin/feedback/inbox', icon: <Inbox size={13} />, label: <Link to="/admin/feedback/inbox">Hộp thư</Link> },
          { key: '/admin/feedback/topics', icon: <MessageCircle size={13} />, label: <Link to="/admin/feedback/topics">Chủ đề lắng nghe</Link> },
        ],
      });
    }
    // V1.0.64: Voice Recording — admin/BOD/recording permissions
    const canViewRecording = hasAnyRole(['SYS_ADMIN', 'BOD'])
      || hasPermission('recording:view_all') || hasPermission('recording:view_dept');
    const canManageDevices = hasPermission('recording:manage_devices');
    if (canViewRecording || canManageDevices) {
      const voiceChildren: any[] = [];
      if (canViewRecording) {
        voiceChildren.push({ key: '/voice-records/dashboard', icon: <LayoutDashboard size={13} />, label: <Link to="/voice-records/dashboard">Tổng quan</Link> });
        voiceChildren.push({ key: '/voice-search', icon: <Search size={13} />, label: <Link to="/voice-search">Tìm trong ghi âm</Link> });
      }
      if (canManageDevices) {
        voiceChildren.push({ key: '/admin/recording-devices', icon: <Cpu size={13} />, label: <Link to="/admin/recording-devices">Quản lý thiết bị</Link> });
      }
      adminChildren.push({
        key: 'voice-menu',
        icon: <Mic size={14} />,
        label: 'Ghi âm thiết bị',
        children: voiceChildren,
      });
    }
    if (hasPermission('system:admin')) {
      adminChildren.push({ key: '/admin/settings', icon: <Cog size={14} />, label: <Link to="/admin/settings">Cài đặt hệ thống</Link> });
    }
    if (adminChildren.length > 0) {
      items.push({ type: 'divider' });
      // Tên label phụ thuộc user có quyền admin hay không
      const hasAdminItem = hasPermission('user:view') || hasPermission('document:manage_categories')
        || hasPermission('audit:view') || hasPermission('system:admin');
      items.push({
        key: 'admin',
        icon: <Settings size={16} />,
        label: hasAdminItem ? 'Quản trị' : 'Hệ thống',
        children: adminChildren,
      });
    }

    return items;
  }, [hasPermission, hasAnyPermission, hasRole, chatUnread]);

  const userMenuItems: MenuProps['items'] = [
    { key: 'profile', icon: <UserIcon size={14} />, label: <Link to="/profile">Hồ sơ cá nhân</Link> },
    { key: 'settings', icon: <Settings size={14} />, label: <Link to="/profile">Cài đặt</Link> },
    { type: 'divider' },
    {
      key: 'logout',
      icon: <LogOut size={14} />,
      label: 'Đăng xuất',
      danger: true,
      onClick: () => {
        logout();
        navigate('/login');
      },
    },
  ];

  const selectedKey = useMemo(() => {
    const path = location.pathname;
    if (path.startsWith('/categories/departments')) return '/categories/departments';
    if (path.startsWith('/categories/task-categories')) return '/categories/task-categories';
    if (path.startsWith('/categories/priorities')) return '/categories/priorities';
    if (path.startsWith('/categories/labels')) return '/categories/labels';
    if (path.startsWith('/categories/print-templates')) return '/categories/print-templates';
    if (path.startsWith('/categories/roles')) return '/categories/roles';
    if (path.startsWith('/tasks')) return '/tasks';
    if (path.startsWith('/signing')) return '/signing';
    if (path.startsWith('/chat')) return '/chat';
    if (path.startsWith('/documents')) return '/documents';
    if (path.startsWith('/templates')) return '/templates';
    if (path.startsWith('/admin/templates')) return '/admin/templates';
    if (path.startsWith('/dashboard/department')) return '/dashboard/department';
    if (path.startsWith('/dashboard/executive')) return '/dashboard/executive';
    if (path.startsWith('/admin/users')) return '/admin/users';
    if (path.startsWith('/admin/categories')) return '/admin/categories';
    if (path.startsWith('/admin/audit-logs')) return '/admin/audit-logs';
    return path;
  }, [location.pathname]);

  // Logo display mode: 'text' = bỏ qua logo upload, dùng text. 'image' = dùng logo nếu có.
  const useTextLogo = settings.logo_display_mode === 'text';
  const showImageLogo = !useTextLogo && hasLogo;

  const logo = (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <div style={{
        width: 32, height: 32, borderRadius: 6,
        background: showImageLogo ? 'transparent' : (settings.primary_color ?? '#6699CC'),
        color: '#fff',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontWeight: 700, fontSize: 13, flexShrink: 0,
        overflow: 'hidden',
      }}>
        {showImageLogo ? (
          <img
            src={`/api/system/logo?v=${logoVersion}`}
            alt="Logo"
            style={{ width: '100%', height: '100%', objectFit: 'contain' }}
          />
        ) : (
          settings.company_short_name ?? 'HV'
        )}
      </div>
      {(!sidebarCollapsed || isMobile) && (
        <div>
          <div style={{ fontWeight: 600, fontSize: 14, color: '#1a1e23' }}>
            {settings.system_name ?? 'HV-TaskFlow'}
          </div>
          <div style={{ fontSize: 11, color: '#7d8a98' }}>
            {settings.company_name ?? 'BV Hưng Việt'}
          </div>
        </div>
      )}
    </div>
  );

  // Tự động detect dark/light từ menu_bg_color (luminance < 0.5 → dark)
  const menuBg = settings.menu_bg_color ?? '#f6f7f8';
  const menuText = settings.menu_text_color || '';  // empty → fallback theo theme dark/light
  const menuActive = settings.menu_active_color || '#1976D2';
  const isMenuDark = (() => {
    const hex = menuBg.replace('#', '');
    if (hex.length < 6) return false;
    const r = parseInt(hex.substring(0, 2), 16) / 255;
    const g = parseInt(hex.substring(2, 4), 16) / 255;
    const b = parseInt(hex.substring(4, 6), 16) / 255;
    const luminance = 0.299 * r + 0.587 * g + 0.114 * b;
    return luminance < 0.5;
  })();

  // Inject CSS variables để override màu menu
  useEffect(() => {
    document.documentElement.style.setProperty('--menu-bg', menuBg);
    if (menuText) document.documentElement.style.setProperty('--menu-text', menuText);
    document.documentElement.style.setProperty('--menu-active', menuActive);
  }, [menuBg, menuText, menuActive]);

  const sidebarMenu = (
    <Menu
      mode="inline"
      theme={isMenuDark ? 'dark' : 'light'}
      selectedKeys={[selectedKey]}
      items={menuItems}
      onClick={() => isMobile && setMobileDrawerOpen(false)}
      className="hv-main-menu"
      style={{ borderRight: 0, background: 'transparent', paddingTop: 8 }}
    />
  );

  // Bottom nav cho mobile
  const bottomNavItems = [
    { key: '/dashboard', icon: <LayoutDashboard size={20} />, label: 'Trang chủ' },
    { key: '/tasks', icon: <ListTodo size={20} />, label: 'Task' },
    { key: 'create', icon: <Plus size={22} />, label: 'Tạo', primary: true },
    { key: '/chat', icon: <MessageCircle size={20} />, label: 'Chat', badge: chatUnread },
    { key: '/notifications', icon: <Bell size={20} />, label: 'Báo', badge: unreadCount },
  ];

  return (
    <Layout style={{ minHeight: '100vh' }}>
      {/* Desktop sidebar */}
      {!isMobile && (
        <Sider
          width={230}
          collapsedWidth={isTablet ? 64 : 64}
          collapsed={sidebarCollapsed || isTablet}
          trigger={null}
          theme={isMenuDark ? 'dark' : 'light'}
          className="hv-app-sider"
          style={{
            borderRight: isMenuDark ? '1px solid #00000033' : '1px solid #d4d9df',
            background: menuBg,
            position: 'sticky',
            top: 0,
            height: '100vh',
            // V1.0.63i: Bỏ overflow auto ở Sider, chuyển vào inner wrapper
            // để header logo + footer trigger luôn cố định, chỉ vùng menu scroll
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Header logo - cố định trên cùng */}
          <div style={{
            height: 56, display: 'flex', alignItems: 'center',
            padding: (sidebarCollapsed || isTablet) ? '0 16px' : '0 20px',
            borderBottom: isMenuDark ? '1px solid #ffffff14' : '1px solid #d4d9df',
            flexShrink: 0,
          }}>
            {logo}
          </div>

          {/* Menu - scroll riêng phần này khi nội dung dài */}
          <div style={{
            flex: 1,
            overflowY: 'auto',
            overflowX: 'hidden',
          }}>
            {sidebarMenu}
          </div>

          {/* V1.0.63i: Nút thu gọn ở footer riêng - không đè lên menu */}
          {!isTablet && (
            <div style={{
              flexShrink: 0,
              borderTop: isMenuDark ? '1px solid #ffffff14' : '1px solid #d4d9df',
              padding: 8,
              display: 'flex',
              justifyContent: (sidebarCollapsed || isTablet) ? 'center' : 'flex-end',
            }}>
              <Button
                type="text"
                size="small"
                icon={sidebarCollapsed
                  ? <ChevronRight size={16} style={{ color: isMenuDark ? '#fff' : '#5b6875' }} />
                  : <ChevronLeft size={16} style={{ color: isMenuDark ? '#fff' : '#5b6875' }} />}
                onClick={toggleSidebar}
                title={sidebarCollapsed ? 'Mở rộng' : 'Thu gọn'}
              />
            </div>
          )}
        </Sider>
      )}

      {/* Mobile drawer sidebar */}
      {isMobile && (
        <Drawer
          open={mobileDrawerOpen}
          onClose={() => setMobileDrawerOpen(false)}
          placement="left"
          width={280}
          styles={{ body: { padding: 0, background: '#fff' } }}
          closeIcon={null}
          extra={<Button type="text" icon={<X size={18} />} onClick={() => setMobileDrawerOpen(false)} />}
          title={logo}
          className="hv-mobile-drawer"
        >
          <Menu
            mode="inline"
            theme="light"
            selectedKeys={[selectedKey]}
            items={menuItems}
            onClick={() => setMobileDrawerOpen(false)}
            style={{ borderRight: 0, background: 'transparent', paddingTop: 8, fontSize: 15, fontWeight: 500 }}
          />
          <div style={{ padding: 16, borderTop: '1px solid #d4d9df', marginTop: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <Avatar
                src={user?.id ? `/api/auth/avatar/${user.id}` : undefined}
                style={{ background: '#2a3547', color: '#6699CC', fontWeight: 600 }}
              >
                {user?.fullName?.slice(-2) ?? 'U'}
              </Avatar>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 700, fontSize: 14, color: '#1a1e23', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {user?.fullName}
                </div>
                <div style={{ fontSize: 12, color: '#5b6875' }}>{user?.position}</div>
              </div>
            </div>
            {/* V1.0.47: Mobile thêm shortcut Hồ sơ + Cài đặt + Đăng xuất
                (trước đây chỉ có nút logout, user không update được avatar/chữ ký trên mobile) */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 12 }}>
              <Button
                block
                icon={<UserIcon size={14} />}
                onClick={() => {
                  setMobileDrawerOpen(false);
                  navigate('/profile');
                }}
                style={{ textAlign: 'left', justifyContent: 'flex-start' }}
              >
                Hồ sơ cá nhân
              </Button>
              <Button
                block
                icon={<Settings size={14} />}
                onClick={() => {
                  setMobileDrawerOpen(false);
                  navigate('/profile');
                }}
                style={{ textAlign: 'left', justifyContent: 'flex-start' }}
              >
                Cài đặt &amp; Chữ ký
              </Button>
              <Button
                block
                danger
                icon={<LogOut size={14} />}
                onClick={() => {
                  logout();
                  navigate('/login');
                }}
                style={{ textAlign: 'left', justifyContent: 'flex-start' }}
              >
                Đăng xuất
              </Button>
            </div>
          </div>
        </Drawer>
      )}

      <Layout>
        <Header style={{
          padding: isMobile ? '0 12px' : '0 24px',
          background: '#fff',
          borderBottom: '1px solid #d4d9df',
          display: 'flex',
          alignItems: 'center',
          gap: isMobile ? 8 : 16,
          position: 'sticky',
          top: 0,
          zIndex: 100,
        }}>
          {isMobile && (
            <Button
              type="text"
              icon={<MenuIcon size={20} />}
              onClick={() => setMobileDrawerOpen(true)}
            />
          )}

          {/* Mobile: show compact logo */}
          {isMobile && !mobileSearchOpen && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1 }}>
              <div style={{
                width: 28, height: 28, borderRadius: 5,
                background: showImageLogo ? 'transparent' : (settings.primary_color ?? '#6699CC'),
                color: '#fff',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontWeight: 700, fontSize: 11, overflow: 'hidden',
              }}>
                {showImageLogo ? (
                  <img
                    src={`/api/system/logo?v=${logoVersion}`}
                    alt="Logo"
                    style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                  />
                ) : (settings.company_short_name ?? 'HV')}
              </div>
              <div style={{ fontWeight: 600, fontSize: 14 }}>
                {settings.system_name ?? 'HV-TaskFlow'}
              </div>
            </div>
          )}

          {/* Search */}
          {!isMobile && (
            <Input
              placeholder="Tìm kiếm task, người, biểu mẫu..."
              prefix={<Search size={14} style={{ color: '#7d8a98' }} />}
              style={{ maxWidth: 420, flex: 1 }}
              allowClear
              value={globalSearch}
              onChange={(e) => setGlobalSearch(e.target.value)}
              onPressEnter={() => handleGlobalSearch(globalSearch)}
            />
          )}
          {isMobile && mobileSearchOpen && (
            <Input
              placeholder="Tìm kiếm..."
              prefix={<Search size={14} />}
              autoFocus
              value={globalSearch}
              onChange={(e) => setGlobalSearch(e.target.value)}
              onPressEnter={() => handleGlobalSearch(globalSearch)}
              onBlur={() => { if (!globalSearch) setMobileSearchOpen(false); }}
              style={{ flex: 1 }}
            />
          )}

          {!isMobile && <div style={{ flex: 1 }} />}

          {/* Mobile: search icon */}
          {isMobile && !mobileSearchOpen && (
            <Button type="text" icon={<Search size={18} />} onClick={() => setMobileSearchOpen(true)} />
          )}

          {/* Create button - desktop inline, mobile hidden (có trong bottom nav) */}
          {!isMobile && (
            <Button type="primary" icon={<Plus size={14} />} onClick={() => navigate('/tasks/new')}>
              Tạo task
            </Button>
          )}

          {!isMobile && <NotificationPermissionButton />}

          <Badge count={unreadCount} size="small" offset={[-2, 2]}>
            <Button
              type="text"
              icon={<Bell size={18} />}
              onClick={() => navigate('/notifications')}
            />
          </Badge>

          {!isMobile && (
            <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
              <div style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8 }}>
                <Avatar
                  src={user?.id ? `/api/auth/avatar/${user.id}` : undefined}
                  style={{ background: '#2a3547', color: '#6699CC', fontWeight: 600 }}
                >
                  {user?.fullName?.slice(-2) ?? 'U'}
                </Avatar>
              </div>
            </Dropdown>
          )}
        </Header>

        <Content style={{
          padding: isMobile ? 12 : 20,
          paddingBottom: isMobile ? 80 : 20,  // Chừa chỗ cho bottom nav
          background: '#f6f7f8',
          overflow: 'auto',
        }}>
          <Outlet />

          {/* Global chat notifier — connect SignalR hub và bắn notification ở mọi trang */}
          <ChatNotifier />
        </Content>

        {/* Bottom navigation cho mobile */}
        {isMobile && (
          <div style={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            height: 64,
            background: '#fff',
            borderTop: '1px solid #d4d9df',
            display: 'grid',
            gridTemplateColumns: 'repeat(5, 1fr)',
            zIndex: 100,
            boxShadow: '0 -2px 12px rgba(0,0,0,0.04)',
          }}>
            {bottomNavItems.map((item) => {
              const active = selectedKey === item.key;
              if (item.key === 'create') {
                return (
                  <button
                    key={item.key}
                    onClick={() => navigate('/tasks/new')}
                    style={{
                      border: 0, background: 'transparent', cursor: 'pointer',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}
                  >
                    <div style={{
                      width: 48, height: 48, borderRadius: '50%', background: '#6699CC', color: '#fff',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      boxShadow: '0 4px 12px rgba(28,80,77,0.3)',
                      marginTop: -18,
                    }}>
                      <Plus size={22} />
                    </div>
                  </button>
                );
              }
              return (
                <button
                  key={item.key}
                  onClick={() => navigate(item.key)}
                  style={{
                    border: 0, background: 'transparent', cursor: 'pointer',
                    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                    gap: 2, padding: 4,
                    color: active ? '#6699CC' : '#7d8a98',
                  }}
                >
                  <Badge count={item.badge ?? 0} size="small" offset={[2, 0]}>
                    {item.icon}
                  </Badge>
                  <span style={{ fontSize: 10, fontWeight: active ? 600 : 400 }}>{item.label}</span>
                </button>
              );
            })}
          </div>
        )}
      </Layout>
    </Layout>
  );
}
